import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Progress } from '~/eds/src/components/Progress/Progress';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { StatusType } from '~/eds/src/types/StatusType';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

interface Props {
  consumedQuota: number;
  max: number;
  isLoading?: boolean;
  mode?: 'default' | 'compact';
  status?: StatusType;
  onClick?: () => void;
}
export const ModelQuotaUsage = ({
  isLoading,
  mode = 'default',
  consumedQuota,
  max,
  status,
  onClick,
}: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const isDefault = mode === 'default';

  const handleClick = () => onClick?.();

  const isOverMax = consumedQuota > max;

  const exceedsMaxColor = isOverMax ? 'status.danger' : 'text.quiet';

  return (
    <Layout
      borderRadius="m"
      direction="column"
      spacing={2}
      py={2}
      px={4}
      ml={-4}
      onClick={handleClick}
    >
      <Box position="absolute" top="50%" left="50%" zIndex={1}>
        <TruncateText variant="tiny" maxW={140}>
          {intl.formatMessage({
            id: 'ModelQuotaUsage.totalModels',
            defaultMessage: 'Total Models:',
            description: 'Label for total models count',
          })}
        </TruncateText>
      </Box>
      <Layout w={isDefault ? undefined : '180px'}>
        <Progress
          isLoading={isLoading}
          name="model-usage"
          max={max}
          status={status}
          statsLabel={{
            position: 'top',
            variant: 'tiny',
            textAlign: 'right',
            color: exceedsMaxColor,
          }}
          value={consumedQuota}
        />
      </Layout>
      {isOverMax && (
        <Text variant="tiny" color="status.danger">
          {isDefault
            ? intl.formatMessage({
                id: 'ModelQuotaUsage.overPurchasedModels',
                defaultMessage: 'Over the purchased number of models.',
                description:
                  'Warning message when exceeding purchased model limit',
              })
            : intl.formatMessage(
                {
                  id: 'ModelQuotaUsage.overCount',
                  defaultMessage: '{count} over',
                  description:
                    'Compact display of how many models over the limit. Example: count = 3',
                },
                {
                  count: formatNumber(consumedQuota - max),
                },
              )}
        </Text>
      )}
    </Layout>
  );
};
